const {ccclass, property} = cc._decorator;

let EventType = cc.Enum({
    ROTATING: 0,
    ROTATE_ENDED: 1,
    TOUCH_UP: 2,
    ROTATE_BEGAN: 3,		
})

@ccclass
export default class EllipticalStage extends cc.Component {
    @property({tooltip: "横向最多能显示多少个子节点"})
    maxShowCount: number = 5

    @property({tooltip: "是否隐藏位于舞台后面的节点"})
    hideBackstage: boolean = false

    @property({type: cc.Node, tooltip: "触摸节点。该节点响应触摸事件"})
    nodeTouch: cc.Node = null

    @property(cc.Float)
    brake: number = 0.03

    @property({type: [cc.Component.EventHandler]})
    rotateStageEvents: cc.Component.EventHandler[] = []

    private _a: number = 0      // 长半轴
    private _b: number = 0      // 短半轴
    private _c: number = 0      // 焦点
    private _v = 0
    private _preTime = 0
    private _touchMoved = false
    private _touchEnded = true

    private _childrenByOrder: Array<cc.Node> = []
    private _childInWait: Array<cc.Node> = []
    
    onLoad () {
        this.node.on(cc.Node.EventType.CHILD_ADDED, this.onAddChild, this)
        this.nodeTouch.on(cc.Node.EventType.TOUCH_START, this.onTouchStart, this, true)
        this.nodeTouch.on(cc.Node.EventType.TOUCH_MOVE, this.onTouchMove, this, true)
        this.nodeTouch.on(cc.Node.EventType.TOUCH_END, this.onTouchEnd, this, true)
        this.nodeTouch.on(cc.Node.EventType.TOUCH_CANCEL, this.onTouchCancel, this, true)
    }

    start () {
        // 因为此时还没适配
        this.node.getComponent(cc.Widget).updateAlignment()
        this._a = this.node.width / 2
        this._b = this.node.height / 2
        this._c = Math.sqrt(Math.pow(this._a, 2) - Math.pow(this._b, 2))
    }

    onDestroy () {
        this.node.off(cc.Node.EventType.CHILD_ADDED, this.onAddChild, this)
        this.nodeTouch.off(cc.Node.EventType.TOUCH_START, this.onTouchStart, this, true)
        this.nodeTouch.off(cc.Node.EventType.TOUCH_MOVE, this.onTouchMove, this, true)
        this.nodeTouch.off(cc.Node.EventType.TOUCH_END, this.onTouchEnd, this, true)
        this.nodeTouch.off(cc.Node.EventType.TOUCH_CANCEL, this.onTouchCancel, this, true)
    }

    update (dt) {
        this.processAutoRotating(dt)
    }

    onAddChild (child: cc.Node) {
        if (child["radian"]) {
            return
        }

        if (this.node.children.length > this.maxShowCount) {
            this._childInWait.push(child)
            child.parent = null
        }else {
            let radian = Math.PI + (Math.PI / (this.maxShowCount - 1) * (this.node.children.length - 1))
            this.setChildPositionByRadian(child, radian)
            this._childrenByOrder.push(child)
        }
    }

    onTouchStart (event: cc.Event.EventTouch) {
        this._v = 0
        this._touchMoved = false
        this._touchEnded = false
        cc.Component.EventHandler.emitEvents(this.rotateStageEvents, this, EventType.ROTATE_BEGAN)
    }

    onTouchMove (event: cc.Event.EventTouch) {
        // 计算夹角
        let radian = event.getDeltaX() / this.node.width * Math.PI
        // 旋转舞台
        this.rotateStage(radian)

        // 计算初速度
        if (this._preTime !== 0) {
            let t = ((new Date().getTime()) - this._preTime) / 1000
            t = t < 0.00001 ? (1 / 60) : t
            this._v = radian / t
        }
        
        this._preTime = new Date().getTime()

        // 滑动超过一段距离，判断为滑动
        let deltaMove = event.touch.getLocation().sub(event.touch.getStartLocation())
        if (deltaMove.mag() > 7) {
            this._touchMoved = true
        }
    }

    onTouchCancel (event: cc.Event.EventTouch) {
        this._preTime = 0
        this._touchEnded = true
        cc.Component.EventHandler.emitEvents(this.rotateStageEvents, this, EventType.TOUCH_UP)
        this._v === 0 && this.stopAutoRotate()
    }

    onTouchEnd (event: cc.Event.EventTouch) {
        this._preTime = 0
        this._touchEnded = true
        cc.Component.EventHandler.emitEvents(this.rotateStageEvents, this, EventType.TOUCH_UP)
        this._v === 0 && this.stopAutoRotate()

        // 不再让子节点获取触摸事件
        if (this._touchMoved) {
            event.stopPropagation();
        }
    }

    stopAutoRotate () {
        this._v = 0
        cc.Component.EventHandler.emitEvents(this.rotateStageEvents, this, EventType.ROTATE_ENDED)
    }

    rotateStage (radian: number) {
        for (let i = 0; i < this.node.children.length; i++) {
            const child = this.node.children[i]
            let newRadian = child["radian"] + radian
            this.setChildPositionByRadian(child, newRadian)
        }

        this.checkAddOrRemoveChild(radian)
        cc.Component.EventHandler.emitEvents(this.rotateStageEvents, this, EventType.ROTATING)
    }

    rotateStageInTime (radian: number, duration: number) {
        let time = 0
        let totalRadian = 0
        let v = radian / duration

        let callback = function (dt) {
            let r = v * dt

            if (totalRadian + r > radian) {
                r = radian - totalRadian
            }
            
            totalRadian += r
            this.rotateStage(r)
            time += dt
            
            if (time > duration) {
                this.unschedule(callback)
                cc.Component.EventHandler.emitEvents(this.rotateStageEvents, this, EventType.ROTATE_ENDED)
            }
        }

        this.schedule(callback, 0)
        cc.Component.EventHandler.emitEvents(this.rotateStageEvents, this, EventType.ROTATE_BEGAN)
    }

    private getPositionByRadian (radian) {
        return cc.v2(this._a * Math.cos(radian), this._b * Math.sin(radian))
    }

    private setChildPositionByRadian (child: cc.Node, radian: number) {
        child["radian"] = radian
        child.position = this.getPositionByRadian(radian)
        child.zIndex = -child.y

        let bottom = -this.node.height * this.node.anchorY
        child.scale = -0.4 * Math.abs(child.y - bottom) / this.node.height + 1

        if (this.hideBackstage) {
            let sinR = Math.sin(radian)
            let sinLine = Math.sin(Math.PI / 7)
            let sinRight = Math.sin(Math.PI / 2)

            if (sinR > -sinRight && sinR < -sinLine) {
                child.opacity = 255
            }else if (sinR > sinLine && sinR < sinRight) {
                child.opacity = 0
            }else {
                child.opacity = (sinLine - sinR) / (2 * sinLine) * 255
            }
        }
    }

    private processAutoRotating (dt) {
        if (this._touchEnded && this._v !== 0) {
            let radian = this._v * dt
            this._v = this._v * (1 - this.brake)

            if (Math.abs(radian) > 0.005) {
                this.rotateStage(radian)
            }else {
                this.stopAutoRotate()
            }
        }
    }

    private checkAddOrRemoveChild (radian: number) {
        let fistChild = this._childrenByOrder[0]
        let lastChild = this._childrenByOrder[this._childrenByOrder.length - 1]

        while (true) {
            let found = false

            if (radian > 0 && fistChild && Math.sin(fistChild["radian"]) < Math.sin(Math.PI)) {
                let node = this._childInWait.shift()
                if (node) {
                    found = true
                    let newRadian = fistChild["radian"] - Math.PI / (this.maxShowCount - 1)
                    this.setChildPositionByRadian(node, newRadian)
                    node.parent = this.node
                    this._childrenByOrder.unshift(node)
                    fistChild = node
                    cc.log("<--左边新增电子:", node["gameConfig"].gameTitleCn)
                }
            }else if (radian < 0 && lastChild && Math.sin(lastChild["radian"]) < 0) {
                let node = this._childInWait.pop()
                if (node) {
                    found = true
                    let newRadian = lastChild["radian"] + Math.PI / (this.maxShowCount - 1)
                    this.setChildPositionByRadian(node, newRadian)
                    node.parent = this.node
                    this._childrenByOrder.push(node)
                    lastChild = node
                    cc.log("-->右边新增电子:", node["gameConfig"].gameTitleCn)
                }
            }

            if (!found) break
        }
        
        while (true) {
            if (radian > 0 && lastChild && lastChild.opacity === 0) {
                this._childInWait.push(lastChild)
                lastChild.parent = null
                this._childrenByOrder.pop()
                lastChild = this._childrenByOrder[this._childrenByOrder.length - 1]
            }else if (radian < 0 && fistChild && fistChild.opacity === 0) {
                this._childInWait.unshift(fistChild)
                fistChild.parent = null
                this._childrenByOrder.shift()
                fistChild = this._childrenByOrder[0]
            }else {
                break
            }
        }
    }
}
